import { PALETTE } from '../lib/palette';
/**
 * Brand assets for Green Bay Bio — Sustainable Bio-Innovation for Indian Farmers.
 * Colors + SVG patterns shared across login, shell, and dashboards.
 *
 * All graphics are inline SVG → data URLs, so no extra HTTP requests and
 * no broken images on Synology / offline / corporate proxy networks.
 */

export const brand = {
  primary: PALETTE.brand[600],
  primaryDark: PALETTE.brand[800],
  primaryLight: PALETTE.brand[400],
  secondary: PALETTE.sky[500],      // matches their "AGN Blue/Green" line
  earth: PALETTE.earth[500],
  harvest: PALETTE.harvest[400],
  background: '#F5F7F4',
  paper: '#FFFFFF',
  tagline: 'Sustainable Bio-Innovation for Indian Farmers',
  taglineHi: 'भारतीय किसानों के लिए सतत जैव-नवाचार',
};

/**
 * Repeating leaf pattern — used as a soft watermark on the menu bar and dashboard.
 * Single leaf rendered at low opacity, tiled.
 */
// Leaf watermark removed from the UI project-wide (looked cluttered). Kept as a
// no-op returning 'none' so existing call sites still compile and render nothing.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const leafPatternBg = (_color = PALETTE.brand[600], _opacity = 0.05): string => 'none';

/**
 * Big organic wave — for the bottom of the login hero panel.
 */
export const heroWaveBg = (): string => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 240' preserveAspectRatio='none'>
    <path fill='rgba(255,255,255,0.08)' d='M0,128 C320,200 720,40 1440,160 L1440,240 L0,240 Z'/>
    <path fill='rgba(255,255,255,0.05)' d='M0,160 C480,240 960,60 1440,200 L1440,240 L0,240 Z'/>
  </svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
};

/**
 * Six brand icons used by the home site — soil, rhizosphere, fertilizer,
 * plant strength, water, weather. Inline SVGs at brand colors.
 */
export const BrandIcons: Record<string, string> = {
  soil: `<svg viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'>
    <path d='M8 40 C20 32 44 32 56 40 L56 56 L8 56 Z' fill={PALETTE.earth[500]}/>
    <circle cx='20' cy='44' r='2' fill='#fff' fill-opacity='.3'/>
    <circle cx='32' cy='48' r='2' fill='#fff' fill-opacity='.3'/>
    <circle cx='44' cy='44' r='2' fill='#fff' fill-opacity='.3'/>
  </svg>`,
  leaf: `<svg viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'>
    <path d='M32 8 C18 18 12 32 12 44 C12 52 20 56 28 54 C42 48 50 32 52 12 C46 12 38 12 32 8 Z' fill={PALETTE.brand[600]}/>
    <path d='M32 12 L26 50' stroke={PALETTE.brand[800]} stroke-width='1.5' fill='none'/>
  </svg>`,
  water: `<svg viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'>
    <path d='M32 8 C24 20 16 32 16 42 C16 50 22 56 32 56 C42 56 48 50 48 42 C48 32 40 20 32 8 Z' fill={PALETTE.sky[500]}/>
  </svg>`,
  weather: `<svg viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='44' cy='20' r='10' fill={PALETTE.harvest[400]}/>
    <path d='M14 38 C14 30 22 26 28 28 C30 22 40 22 42 28 C50 28 54 38 48 42 L18 42 C12 42 12 38 14 38 Z' fill='#fff' stroke='#888' stroke-width='1'/>
  </svg>`,
  plant: `<svg viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'>
    <path d='M32 56 L32 24' stroke={PALETTE.brand[800]} stroke-width='2' fill='none'/>
    <path d='M32 32 C20 28 14 18 18 12 C24 14 32 22 32 32 Z' fill={PALETTE.brand[400]}/>
    <path d='M32 28 C44 24 50 14 46 8 C40 10 32 18 32 28 Z' fill={PALETTE.brand[600]}/>
    <path d='M14 56 L50 56' stroke={PALETTE.earth[500]} stroke-width='3'/>
  </svg>`,
  bio: `<svg viewBox='0 0 64 64' xmlns='http://www.w3.org/2000/svg'>
    <circle cx='32' cy='32' r='20' fill='none' stroke={PALETTE.brand[600]} stroke-width='2'/>
    <path d='M22 32 C22 24 28 18 32 18 C36 18 42 24 42 32 C42 40 36 46 32 46 C28 46 22 40 22 32 Z' fill='none' stroke={PALETTE.brand[600]} stroke-width='2'/>
    <circle cx='32' cy='32' r='4' fill={PALETTE.brand[400]}/>
  </svg>`,
};

export const inlineSvgDataUrl = (svgString: string): string =>
  `data:image/svg+xml;utf8,${encodeURIComponent(svgString)}`;
